import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ConflictError } from '../api/client';
import type { EmployeeRosterRow } from '../api/types';
import { useApp } from '../state/AppContext';
import { ConflictBanner, EmptyRow, ErrorBanner, SortableHeader, useTableSort } from '../components/ui';

/**
 * The employee master, which until now had no screen at all — 115 people you
 * could import but never look at.
 *
 * The HR export owns this data. Adding someone here exists because a person
 * missing from an export has punch rows going nowhere, and waiting for a
 * corrected export can take days. But the import always wins: it MERGEs on
 * Employee ID and overwrites name, department and location unconditionally, so
 * anything typed here survives exactly until the next employees import. The
 * screen says that out loud rather than letting someone believe a fix stuck.
 */

type EmployeeRow = EmployeeRosterRow;

interface Draft {
  employeeId: string;
  name: string;
  department: string;
  locationCode: string;
  title: string;
  isActive: boolean;
  rowVersionNo: number;
}

const emptyDraft = (): Draft => ({
  employeeId: '', name: '', department: '', locationCode: '', title: '', isActive: true, rowVersionNo: 0,
});

export function Employees() {
  const queryClient = useQueryClient();
  const { reference, scopedDepartments, scopedFacilities, can } = useApp();
  const canEdit = can('employees', 'edit');

  const [search, setSearch] = useState('');
  const [department, setDepartment] = useState('all');
  const [location, setLocation] = useState('all');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [showAll, setShowAll] = useState(false);
  const [bulkDept, setBulkDept] = useState('');
  const [bulkLoc, setBulkLoc] = useState('');
  const [bulkStatus, setBulkStatus] = useState('');

  // Settings → Scorecard Scope governs what is listed. This hides rows only;
  // every employee stays in the database and keeps their full record. "Show
  // everyone" also lists people marked Terminated, so they can be found and
  // brought back rather than vanishing from the only screen that edits them.
  const list = useQuery({
    queryKey: ['employees-admin', showAll],
    queryFn: () => api.get<{ rows: EmployeeRow[]; outOfScopeHidden: number }>(
      '/api/employees', { take: 5000, scopeOnly: !showAll, includeInactive: showAll }),
  });

  const done = (message: string) => {
    setDraft(null);
    setError(null);
    setConflict(false);
    setSaved(message);
    queryClient.invalidateQueries();
    setTimeout(() => setSaved(null), 6000);
  };

  // Only an edit can go stale under someone. A 409 on add ("already exists") or
  // delete ("has reviewed history") is the API explaining a refusal, so its
  // own message is shown rather than the concurrency banner.
  const fail = (e: unknown) => setError(e instanceof Error ? e.message : 'Save failed.');
  const failUpdate = (e: unknown) => {
    if (e instanceof ConflictError) { setConflict(true); return; }
    fail(e);
  };

  const create = useMutation({
    mutationFn: (d: Draft) => api.post('/api/employees', {
      employeeId: Number(d.employeeId),
      name: d.name,
      department: d.department || null,
      locationCode: d.locationCode || null,
      title: d.title || null,
      isActive: d.isActive,
      rowVersionNo: 0,
    }),
    onSuccess: (_r, d) => done(`Employee ${d.employeeId} added.`),
    onError: fail,
  });

  const update = useMutation({
    mutationFn: (d: Draft) => api.put(`/api/employees/${d.employeeId}`, {
      employeeId: Number(d.employeeId),
      name: d.name,
      department: d.department || null,
      locationCode: d.locationCode || null,
      title: d.title || null,
      isActive: d.isActive,
      rowVersionNo: d.rowVersionNo,
    }),
    onSuccess: (_r, d) => done(`Employee ${d.employeeId} updated.`),
    onError: failUpdate,
  });

  const remove = useMutation({
    mutationFn: (r: EmployeeRow) =>
      api.del<{ dailyRecordsRemoved: number; punchesKept: number; scheduleRemoved: boolean }>(
        `/api/employees/${r.employeeId}`),
    onSuccess: (res, r) => {
      const bits: string[] = [];
      if (res.scheduleRemoved) bits.push('schedule removed');
      if (res.dailyRecordsRemoved) bits.push(`${res.dailyRecordsRemoved} daily records removed`);
      if (res.punchesKept) bits.push(`${res.punchesKept} punch rows kept, now unmatched`);
      done(`${r.sortName} deleted${bits.length ? ' — ' + bits.join(', ') : ''}.`);
    },
    onError: fail,
  });

  // One request, not one per employee.
  const bulkSave = useMutation({
    mutationFn: (ids: number[]) =>
      api.put<{ applied: number }>('/api/employees/bulk', {
        employeeIds: ids,
        department: bulkDept || null,
        locationCode: bulkLoc || null,
        isActive: bulkStatus === '' ? null : bulkStatus === 'active',
      }),
    onSuccess: (r) => {
      setSelected(new Set());
      setBulkDept(''); setBulkLoc(''); setBulkStatus('');
      done(`Updated ${r.applied} ${r.applied === 1 ? 'employee' : 'employees'}.`);
    },
    onError: fail,
  });

  const rows = useMemo(() => {
    const all = list.data?.rows ?? [];
    const q = search.trim().toLowerCase();
    return all.filter((r) =>
      (q === '' || r.sortName.toLowerCase().includes(q) || String(r.employeeId).includes(q))
      && (department === 'all'
          || (department === '__none__' ? !r.department : r.department === department))
      && (location === 'all'
          || (location === '__none__' ? !r.locationCode : r.locationCode === location)));
  }, [list.data, search, department, location]);

  const tableSort = useTableSort(rows, {
    id: (r) => r.employeeId,
    name: (r) => r.sortName,
    department: (r) => r.department,
    location: (r) => r.locationCode,
    title: (r) => r.title,
    supervisor: (r) => r.supervisor,
    schedule: (r) => r.hasSchedule,
    status: (r) => r.isActive,
  });

  const isNew = draft !== null && draft.rowVersionNo === 0 && !rows.some((r) => String(r.employeeId) === draft.employeeId);
  const missingLocation = (list.data?.rows ?? []).filter((r) => !r.locationCode && r.isActive).length;
  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Employees</div>
          <div className="page-subtitle">
            {!list.data ? '…'
              : showAll ? `${list.data.rows.length} in the employee master, including terminated`
              : `${list.data.rows.length} active in the scorecard scope`
                + (list.data.outOfScopeHidden > 0 ? ` · ${list.data.outOfScopeHidden} outside it hidden` : '')}
            {' '}· sourced from the HR export
          </div>
        </div>
        {canEdit && (
          <button className="btn btn--primary" onClick={() => { setDraft(emptyDraft()); setError(null); }}>
            Add employee
          </button>
        )}
      </div>

      <div className="banner banner--info" style={{ marginBottom: 14 }}>
        <strong>Two ways in, one set of records.</strong> Employees can be entered and maintained here directly, or
        loaded from the HR export — both write the same rows. If you do run an employees import, it wins: Employee ID
        is matched and name, department, location and status are replaced by whatever the file says.
      </div>

      {missingLocation > 0 && (
        <div className="banner banner--conflict" style={{ marginBottom: 14 }}>
          <strong>{missingLocation} {missingLocation === 1 ? 'employee has' : 'employees have'} no location.</strong>{' '}
          They import without error but drop out of location-scoped dashboards and Location Manager visibility.
        </div>
      )}

      {conflict && (
        <ConflictBanner
          message="This employee was changed by someone else while you were editing."
          onReload={() => { setConflict(false); queryClient.invalidateQueries(); }}
        />
      )}
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      {saved && <div className="banner banner--info">{saved}</div>}

      {draft && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, marginBottom: 12 }}>
            {isNew ? 'Add employee' : `Edit employee ${draft.employeeId}`}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 12 }}>
            <label>
              <div className="field-label">EMPLOYEE ID</div>
              <input
                className="control"
                type="number"
                value={draft.employeeId}
                disabled={!isNew}
                onChange={(e) => setDraft({ ...draft, employeeId: e.target.value })}
              />
            </label>
            <label style={{ gridColumn: 'span 2' }}>
              <div className="field-label">NAME — “Last, First”</div>
              <input
                className="control"
                value={draft.name}
                placeholder="Trout, Cody R"
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </label>
            <label>
              <div className="field-label">DEPARTMENT</div>
              <input
                className="control"
                list="department-options"
                value={draft.department}
                onChange={(e) => setDraft({ ...draft, department: e.target.value })}
              />
              <datalist id="department-options">
                {reference.departments.map((d) => <option key={d} value={d} />)}
              </datalist>
            </label>
            <label>
              <div className="field-label">LOCATION</div>
              <select
                className="control"
                value={draft.locationCode}
                onChange={(e) => setDraft({ ...draft, locationCode: e.target.value })}
              >
                <option value="">— none —</option>
                {reference.facilities.map((f) => <option key={f.code} value={f.code}>{f.code}</option>)}
              </select>
            </label>
            <label>
              <div className="field-label">JOB TITLE</div>
              <input
                className="control"
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              />
            </label>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={draft.isActive}
              onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
            />
            Active — an inactive employee generates no daily records and leaves the scorecard
          </label>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button className="btn" onClick={() => { setDraft(null); setError(null); }}>Cancel</button>
            <button
              className="btn btn--primary"
              disabled={create.isPending || update.isPending || !draft.name.trim() || !draft.employeeId}
              onClick={() => (isNew ? create.mutate(draft) : update.mutate(draft))}
            >
              {create.isPending || update.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {selected.size > 0 && canEdit && (
        <div className="card" style={{ marginBottom: 16, borderColor: 'var(--blue-border)' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
            <div className="section-label" style={{ marginBottom: 0 }}>
              EDIT {selected.size} {selected.size === 1 ? 'EMPLOYEE' : 'EMPLOYEES'}
            </div>
            <button className="btn btn--sm" style={{ border: 'none', background: 'transparent', color: 'var(--note)' }}
              onClick={() => setSelected(new Set())}>
              Deselect all
            </button>
          </div>
          <div className="meta" style={{ marginBottom: 12 }}>
            Leave a field on <em>Leave unchanged</em> and it is not touched — setting locations will not disturb departments.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 14 }}>
            <label>
              <div className="field-label">DEPARTMENT</div>
              <select className="control" value={bulkDept} onChange={(e) => setBulkDept(e.target.value)}>
                <option value="">Leave unchanged</option>
                {reference.departments.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </label>
            <label>
              <div className="field-label">LOCATION</div>
              <select className="control" value={bulkLoc} onChange={(e) => setBulkLoc(e.target.value)}>
                <option value="">Leave unchanged</option>
                {reference.facilities.map((f) => <option key={f.code} value={f.code}>{f.code}</option>)}
              </select>
            </label>
            <label>
              <div className="field-label">STATUS</div>
              <select className="control" value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value)}>
                <option value="">Leave unchanged</option>
                <option value="active">Active</option>
                <option value="terminated">Terminated</option>
              </select>
            </label>
          </div>
          <button
            className="btn btn--primary"
            disabled={bulkSave.isPending || (!bulkDept && !bulkLoc && !bulkStatus)}
            onClick={() => bulkSave.mutate([...selected])}
          >
            {bulkSave.isPending ? 'Applying…' : `Apply to ${selected.size} ${selected.size === 1 ? 'employee' : 'employees'}`}
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <input
          className="control"
          style={{ maxWidth: 260 }}
          placeholder="Search name or Employee ID"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="control" style={{ maxWidth: 220 }} value={department} onChange={(e) => setDepartment(e.target.value)}>
          <option value="all">All departments</option>
          <option value="__none__">— No department set —</option>
          {(showAll ? reference.departments : scopedDepartments).map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, color: 'var(--text-secondary)' }}>
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
          Show everyone, including terminated
          {(list.data?.outOfScopeHidden ?? 0) > 0 && !showAll && (
            <span className="meta">({(list.data?.outOfScopeHidden ?? 0)} outside scorecard settings)</span>
          )}
        </label>
        <select className="control" style={{ maxWidth: 200 }} value={location} onChange={(e) => setLocation(e.target.value)}>
          <option value="all">All locations</option>
          <option value="__none__">— No location set —</option>
          {(showAll ? reference.facilities : scopedFacilities).map((f) => <option key={f.code} value={f.code}>{f.code}</option>)}
        </select>
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              {canEdit && (
                <th style={{ width: 28 }}>
                  <input
                    type="checkbox"
                    aria-label="Select all shown"
                    title="Select every employee currently listed"
                    checked={rows.length > 0 && selected.size === rows.length}
                    ref={(el) => { if (el) el.indeterminate = selected.size > 0 && selected.size < rows.length; }}
                    onChange={(e) => setSelected(e.target.checked ? new Set(rows.map((r) => r.employeeId)) : new Set())}
                  />
                </th>
              )}
              <SortableHeader label="ID" column="id" sort={tableSort.sort} onSort={tableSort.onSort} className="num" />
              <SortableHeader label="NAME" column="name" sort={tableSort.sort} onSort={tableSort.onSort} />
              <SortableHeader label="DEPARTMENT" column="department" sort={tableSort.sort} onSort={tableSort.onSort} />
              <SortableHeader label="LOCATION" column="location" sort={tableSort.sort} onSort={tableSort.onSort} />
              <SortableHeader label="JOB TITLE" column="title" sort={tableSort.sort} onSort={tableSort.onSort} />
              <SortableHeader label="SUPERVISOR" column="supervisor" sort={tableSort.sort} onSort={tableSort.onSort} />
              <SortableHeader label="SCHEDULE" column="schedule" sort={tableSort.sort} onSort={tableSort.onSort} />
              <SortableHeader label="STATUS" column="status" sort={tableSort.sort} onSort={tableSort.onSort} />
              {canEdit && <th />}
            </tr>
          </thead>
          <tbody>
            {list.isLoading && <EmptyRow colSpan={canEdit ? 10 : 8}>Loading…</EmptyRow>}
            {list.isError && <EmptyRow colSpan={canEdit ? 10 : 8}>{(list.error as Error).message}</EmptyRow>}
            {!list.isLoading && !list.isError && rows.length === 0 && (
              <EmptyRow colSpan={canEdit ? 10 : 8}>
                {(list.data?.rows.length ?? 0) === 0
                  ? 'No employees yet. Import an employees file, or add one by hand.'
                  : 'No employees match those filters.'}
              </EmptyRow>
            )}
            {tableSort.rows.map((r) => (
              <tr key={r.employeeId} style={r.isActive ? undefined : { color: 'var(--muted)' }}>
                {canEdit && (
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(r.employeeId)}
                      aria-label={`Select ${r.sortName}`}
                      onChange={() => setSelected((prev) => {
                        const next = new Set(prev);
                        next.has(r.employeeId) ? next.delete(r.employeeId) : next.add(r.employeeId);
                        return next;
                      })}
                    />
                  </td>
                )}
                <td className="num">{r.employeeId}</td>
                <td className="strong">{r.sortName}</td>
                <td>{r.department ?? '—'}</td>
                <td style={!r.locationCode ? { color: 'var(--orange-deep)', fontWeight: 600 } : undefined}>
                  {r.locationCode ?? 'not set'}
                </td>
                <td>{r.title ?? '—'}</td>
                <td>{r.supervisor ?? '—'}</td>
                <td className="meta">{r.hasSchedule ? 'yes' : 'none'}</td>
                <td>{r.isActive ? 'Active' : <span className="meta">Terminated</span>}</td>
                {canEdit && (
                  <td className="num">
                    <button
                      className="btn btn--sm"
                      onClick={() => {
                        setError(null);
                        setDraft({
                          employeeId: String(r.employeeId),
                          name: r.sortName,
                          department: r.department ?? '',
                          locationCode: r.locationCode ?? '',
                          title: r.title ?? '',
                          isActive: r.isActive,
                          rowVersionNo: r.rowVersionNo,
                        });
                      }}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn--sm"
                      style={{ marginLeft: 6, color: 'var(--red)' }}
                      disabled={remove.isPending}
                      onClick={() => {
                        const msg = `Delete ${r.sortName} (${r.employeeId})?`
                          + `\n\nFor someone entered by mistake. Their schedule and unreviewed daily records go too.`
                          + `\nPunch rows are kept and become unmatched, so they count again if this employee is re-added.`
                          + `\n\nSomeone with reviewed or locked-period history cannot be deleted — mark them Terminated instead.`;
                        if (window.confirm(msg)) remove.mutate(r);
                      }}
                    >
                      Delete
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
